import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { ensureFreshToken, triggerSignIn } from '@/common/composables/useAuthGuard';
import { getUrls } from '@/common/composables/url/url';

function getParleyBaseUrl(): string {
  const { apiUrlBase } = getUrls();
  return apiUrlBase;
}

const parleyHttpClient = axios.create({
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

parleyHttpClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  config.baseURL = getParleyBaseUrl();

  const token = await ensureFreshToken();
  if (!token) {
    // Abort before the request leaves the browser; ensureFreshToken has
    // already triggered a (deduped) Okta redirect. Throwing axios.Cancel
    // means downstream catches see axios.isCancel(err) === true and can
    // skip error toasts.
    throw new axios.Cancel('No valid auth token; sign-in in progress');
  }

  config.headers.Authorization = `Bearer ${token}`;

  return config;
});

parleyHttpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Await so the redirect actually starts before the rejection bubbles
      // up to per-call .catch handlers. triggerSignIn is deduped, so
      // concurrent 401s share one redirect.
      await triggerSignIn();
    }
    return Promise.reject(error);
  }
);

export default parleyHttpClient;
